import React, { useState } from "react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Checkbox,
} from "@material-ui/core"
import CheckIcon from "@material-ui/icons/Check"
import CloseIcon from "@material-ui/icons/Close"
import moment from "moment"
import PropTypes from "prop-types"
import classNames from "classnames"
import useStyles from "./styles"
import LoadingButton from "../../../LoadingButton/index"
import GreenButton from "../../Common/GreenButton/index"
import { useDispatch } from "react-redux"
import {
  toggleSendBillingDialog,
  toggleClickConfirmationDialog,
} from "../../../../actions/billingDialogs"
import { openSnackBar } from "../../../../actions/snack-bar"
import {
  sendSolo,
  updateSoloStatus,
  updateSolo,
} from "../../../../actions/solos"
import formatNumberWithCommas from "../../../../utils/formatNumberWithCommas"
import BillingClientTerms from "../BillingClientTerms/index"
import { markPaymentReceived } from "../../../../actions/billing"
import capitalizeFirstLetter from "../../../../utils/capitalizeFirstLetter"
import { Tooltip } from "../../../../../node_modules/@material-ui/core/index"

const SoloAdminActions = ({
  type = "",
  soloData = {},
  setSoloData = () => {},
  amounts = {},
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const soloId = soloData && soloData[`${type}_id`]
  const emailSent = soloData && Boolean(soloData.sent)
  const clientAccepted = soloData && Boolean(soloData.client_accepted)
  const clientRejected = soloData && Boolean(soloData.client_rejected)
  const connectedToShoot = soloData && Boolean(soloData.shoot_id)

  const clientPaymentSent =
    soloData && soloData.first_payment_type && !Boolean(soloData.full_paid)
  const paymentAccepted = soloData && Boolean(soloData.full_paid)

  const [emailLoading, toggleEmailLoading] = useState(false)
  const [acceptedLoading, toggleAcceptedLoading] = useState(false)
  const [checkboxLoading, toggleCheckboxLoading] = useState(false)

  const sendDisabled = soloData && soloData.client_id === null

  const sendEmailFunc = dialogVals => {
    toggleEmailLoading(true)
    // save before send email
    dispatch(updateSolo(type, soloId, { ...soloData }))
      .then(() => {
        // send email
        const requestData = {
          email_to: dialogVals.recipients.join(", "),
          subject: dialogVals.subject,
          message: dialogVals.message,
        }
        dispatch(sendSolo(type, soloId, requestData))
          .then(() => {
            setSoloData({
              ...soloData,
              sent: 1,
              sent_to: requestData.email_to,
              [`${type}_sent_date`]: moment().format(),
            })
            toggleEmailLoading(false)
            dispatch(openSnackBar({ message: "Email Sent Successfully" }))
          })
          .catch(err => {
            console.log(err)
            dispatch(openSnackBar({ message: "Failed to Send Email" }))
            toggleEmailLoading(false)
          })
      })
      .catch(err => {
        console.log(err)
        toggleEmailLoading(false)
      })
  }

  const handleEmailSoloDialog = () => {
    let defaultEmail =
      soloData.sent_to || soloData.client_email
        ? (soloData.sent_to || soloData.client_email)
            .split(",")
            .map(x => x.trim())
        : null
    let dialogData = {
      type: type,
      shootName: soloData.client_name,
      defaultEmail: defaultEmail,
      sendFunc: sendEmailFunc,
    }
    dispatch(toggleSendBillingDialog(true, dialogData))
  }

  const adminMarkAccepted = () => {
    toggleAcceptedLoading(true)
    let request = {
      client_terms_accepted: 1,
      client_accepted: 1,
      client_signature: "admin",
    }
    dispatch(updateSoloStatus(type, soloId, request))
      .then(() => {
        setSoloData({
          ...soloData,
          client_terms_accepted: 1,
          client_accepted: 1,
          client_signature: "admin",
          [`${type}_accepted_date`]: moment().format(),
        })
        toggleAcceptedLoading(false)
        dispatch(openSnackBar({ message: `Marked this ${type} as accepted` }))
      })
      .catch(err => {
        console.log(err)
        toggleAcceptedLoading(false)
        dispatch(
          openSnackBar({ message: `Error marking this ${type} as accepted` })
        )
      })
  }

  const handleConfirmAdminAccept = () => {
    let dialogData = {
      action: `accept this ${type} on behalf of the client`,
      confirmationFunc: adminMarkAccepted,
      funcArgs: [],
    }
    dispatch(toggleClickConfirmationDialog(true, dialogData))
  }

  const markCheckAsReceived = () => {
    toggleCheckboxLoading(true)
    let invoiceId = soloData.client_invoice_id
    let callData = {
      paidColumn: "full_paid",
      paymentStatus: paymentAccepted ? 0 : 1,
    }
    dispatch(markPaymentReceived(callData, invoiceId))
      .then(() => {
        setSoloData(oldState => {
          return {
            ...oldState,
            full_paid: paymentAccepted ? 0 : 1,
            full_paid_date: paymentAccepted ? null : moment().format(),
          }
        })
        toggleCheckboxLoading(false)
        dispatch(
          openSnackBar({
            message: `Marked solo invoice payment as ${
              paymentAccepted ? "not received" : "received"
            }`,
          })
        )
      })
      .catch(err => {
        console.log(err)
        toggleCheckboxLoading(false)
        dispatch(
          openSnackBar({
            message: "Unable to change final payment received status",
          })
        )
      })
  }

  return (
    <Box
      mt={2}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
    >
      {type === "invoice" &&
        (!emailSent && !clientAccepted) && (
          <Card style={{ width: "100%", marginTop: 0, marginBottom: 30 }}>
            <CardContent>
              <BillingClientTerms data={soloData} setData={setSoloData} />
            </CardContent>
          </Card>
        )}
      {(emailSent || clientAccepted) && (
        <Card className={classes.card}>
          <CardContent>
            <Box display="flex" flexDirection="column" mb={3}>
              <Box className={classes.rowWrapper}>
                <Typography variant="h3">
                  {clientAccepted
                    ? `${capitalizeFirstLetter(type)} Accepted`
                    : clientRejected
                      ? `${capitalizeFirstLetter(type)} Rejected`
                      : "Pending Approval"}
                </Typography>
                <Box
                  className={classNames(classes.statusDot, {
                    [classes.acceptedDot]: clientAccepted,
                    [classes.rejectedDot]: clientRejected,
                  })}
                >
                  {clientAccepted && <CheckIcon className={classes.icon} />}
                  {clientRejected && <CloseIcon className={classes.icon} />}
                </Box>
              </Box>
              {clientAccepted ? (
                <Typography className={classes.grayFont}>
                  Accepted{" "}
                  {moment(soloData[`${type}_accepted_date`]).format(
                    "M/D/YY[ @ ]h:mm A"
                  )}
                </Typography>
              ) : (
                <Typography className={classes.grayFont}>
                  Sent{" "}
                  {moment(soloData[`${type}_sent_date`]).format(
                    "M/D/YY[ @ ]h:mm A"
                  )}
                </Typography>
              )}
            </Box>
            {/* INVOICE ONLY - tracking on payment status */}
            {type.toLowerCase() === "invoice" && (
              <Box display="flex" flexDirection="column">
                <Box className={classes.rowWrapper}>
                  <Typography variant="h3" align="left">
                    Payment Received
                  </Typography>
                  <Box
                    className={classNames(classes.statusDot, {
                      [classes.acceptedDot]: paymentAccepted,
                    })}
                  >
                    {paymentAccepted && <CheckIcon className={classes.icon} />}
                  </Box>
                </Box>
                <Box mt={1} mb={1} className={classes.rowWrapper}>
                  <Typography variant="h2" className={classes.greenText}>
                    Amount Due
                  </Typography>
                  <Typography variant="h2" className={classes.greenText}>
                    $ {formatNumberWithCommas(amounts.remaining)}
                  </Typography>
                </Box>
                <Typography className={classes.grayFont}>
                  {soloData.payment_terms_name} Payment Terms
                </Typography>
                {(clientPaymentSent || paymentAccepted) && (
                  <Box>
                    <Typography className={classes.grayFont}>
                      Paid via {soloData && soloData.first_payment_type}
                    </Typography>
                    {soloData.first_payment_type === "check" && (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="25px"
                      >
                        {checkboxLoading ? (
                          <Box pl={2} pr={2} pt={1.5} pb={1.5}>
                            <CircularProgress size={16} />
                          </Box>
                        ) : (
                          <Checkbox
                            checked={paymentAccepted}
                            onChange={markCheckAsReceived}
                          />
                        )}
                        <Typography className={classes.grayFont}>
                          Mark payment{" "}
                          {soloData && soloData.full_paid === 1
                            ? "not received"
                            : "received"}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
            {/* ESTIMATE ONLY - just bare bones total */}
            {type.toLowerCase() === "estimate" && (
              <Box display="flex" flexDirection="column">
                <Box className={classes.rowWrapper}>
                  <Typography variant="h3" align="left">
                    Estimate Total
                  </Typography>
                  <Typography variant="h2" className={classes.greenText}>
                    $ {formatNumberWithCommas(amounts.remaining)}
                  </Typography>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
      <Box className={classes.buttonContainer}>
        <Tooltip title={sendDisabled ? "Attach a client to solo first" : ""}>
          <Box width="100%">
            <GreenButton
              fullWidth
              variant="contained"
              onClick={handleEmailSoloDialog}
              disabled={connectedToShoot || emailLoading || sendDisabled}
            >
              {emailLoading ? (
                <CircularProgress size={24} style={{ color: "#fff" }} />
              ) : emailSent ? (
                `Resend ${capitalizeFirstLetter(type)}`
              ) : (
                `Send ${capitalizeFirstLetter(type)}`
              )}
            </GreenButton>
          </Box>
        </Tooltip>
        {!clientAccepted && (
          <Tooltip title={sendDisabled ? "Attach a client to solo first" : ""}>
            <Box width="100%">
              <LoadingButton
                fullWidth
                variant="contained"
                onClick={handleConfirmAdminAccept}
                className={classes.button}
                disabled={connectedToShoot || acceptedLoading || sendDisabled}
              >
                {acceptedLoading ? (
                  <CircularProgress size={24} style={{ color: "#fff" }} />
                ) : (
                  "Mark As Accepted"
                )}
              </LoadingButton>
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  )
}

SoloAdminActions.propTypes = {
  type: PropTypes.string,
  soloData: PropTypes.object,
  setSoloData: PropTypes.func,
  amounts: PropTypes.object,
}

export default SoloAdminActions
